<template>
  <div
    v-if="!decision"
    class="decision-detail-panel bg-white rounded-[12px] p-8 shadow-sm border border-[#E5E5E5] flex items-center justify-center min-h-[300px]"
    data-testid="decision-detail-panel"
  >
    <p class="text-[13px] text-[var(--color-disabled)] m-0">
      {{ $t('UserProjects.decisions.detail.empty') }}
    </p>
  </div>

  <div
    v-else
    class="decision-detail-panel bg-white rounded-[12px] p-5 shadow-sm border border-[#E5E5E5]"
    data-testid="decision-detail-panel"
  >
    <div class="flex items-start justify-between gap-3 mb-4 pb-4 border-b border-[#F0F0F0]">
      <div class="flex-1 min-w-0">
        <h2 class="text-[18px] font-bold text-[var(--color-dark)] mb-1 m-0">{{ decision.title }}</h2>
        <p class="text-[12px] text-[var(--color-disabled)] m-0">{{ decision.projectName }}</p>
      </div>
      <div class="flex items-start gap-2 shrink-0">
        <div
          v-if="hasActions"
          class="flex items-center gap-1"
          data-testid="decision-detail-actions"
        >
          <button
            v-if="isEditable"
            type="button"
            class="decision-detail-panel__action"
            :title="$t('ProjectManagement.decisions.editTitle')"
            data-testid="decision-detail-edit"
            @click="$emit('edit', decision)"
          >
            <i class="pi pi-pencil" aria-hidden="true" />
          </button>
          <button
            v-if="decision.status === 'open'"
            type="button"
            class="decision-detail-panel__action"
            :title="$t('ProjectManagement.decisions.actions.markPrepared')"
            data-testid="decision-detail-mark-prepared"
            @click="$emit('mark-status', { decision, status: 'prepared' })"
          >
            <i class="pi pi-file-edit" aria-hidden="true" />
          </button>
          <button
            v-if="isPending"
            type="button"
            class="decision-detail-panel__action decision-detail-panel__action--primary"
            :title="$t('ProjectManagement.decisions.actions.markDecided')"
            data-testid="decision-detail-mark-decided"
            @click="$emit('mark-status', { decision, status: 'decided' })"
          >
            <i class="pi pi-check" aria-hidden="true" />
          </button>
          <button
            v-if="isPending"
            type="button"
            class="decision-detail-panel__action decision-detail-panel__action--warn"
            :title="$t('ProjectManagement.decisions.actions.escalate')"
            data-testid="decision-detail-escalate"
            @click="$emit('escalate', decision)"
          >
            <i class="pi pi-arrow-up" aria-hidden="true" />
          </button>
          <button
            v-if="isEditable"
            type="button"
            class="decision-detail-panel__action decision-detail-panel__action--danger"
            :title="$t('ProjectManagement.decisions.deleteTitle')"
            data-testid="decision-detail-delete"
            @click="$emit('delete', decision)"
          >
            <i class="pi pi-trash" aria-hidden="true" />
          </button>
        </div>
        <span
          class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0"
          :style="statusStyle"
        >
          {{ statusLabel }}
        </span>
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <p class="decision-detail-panel__label">{{ $t('UserProjects.decisions.detail.decision') }}</p>
        <div class="text-[13px] text-[var(--color-dark)] leading-relaxed">{{ decision.title }}</div>
      </div>
      <div>
        <p class="decision-detail-panel__label">{{ $t('UserProjects.decisions.detail.responsibility') }}</p>
        <div class="text-[13px] text-[var(--color-dark)] leading-relaxed">
          <p class="m-0 mb-1">
            <span class="decision-detail-panel__inline-label">{{ $t('UserProjects.decisions.detail.decider') }}:</span>
            {{ deciderLabel }}
          </p>
          <p class="m-0">
            <span class="decision-detail-panel__inline-label">{{ $t('UserProjects.decisions.detail.preparer') }}:</span>
            {{ preparerLabel }}
          </p>
        </div>
      </div>
      <div>
        <p class="decision-detail-panel__label">{{ $t('UserProjects.decisions.detail.basis') }}</p>
        <div class="text-[13px] text-[var(--color-dark)] leading-relaxed">{{ decision.basis || '—' }}</div>
      </div>
      <div>
        <p class="decision-detail-panel__label">{{ $t('UserProjects.decisions.detail.dueDate') }}</p>
        <div
          class="text-[13px] leading-relaxed"
          :class="decision.isOverdue ? 'text-[var(--color-error-text)] font-medium' : 'text-[var(--color-dark)]'"
        >
          {{ dueLine }}
        </div>
      </div>
      <div>
        <p class="decision-detail-panel__label">{{ $t('UserProjects.decisions.detail.impact') }}</p>
        <div class="text-[13px] text-[var(--color-error-text)] font-medium leading-relaxed">
          {{ decision.impactIfDelayed || '—' }}
        </div>
      </div>
      <div>
        <p class="decision-detail-panel__label">{{ $t('UserProjects.decisions.detail.context') }}</p>
        <div class="text-[13px] text-[var(--color-dark)] leading-relaxed">
          <p class="m-0">{{ decision.milestoneTitle || '—' }}</p>
          <p class="text-[12px] text-[var(--color-disabled)] mt-0.5 mb-0">{{ decision.projectName }}</p>
        </div>
      </div>
    </div>

    <div class="mt-5 pt-4 border-t border-[#F0F0F0] flex items-center gap-4">
      <div>
        <p class="decision-detail-panel__label mb-0.5">{{ $t('UserProjects.decisions.detail.status') }}</p>
        <span
          class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
          :style="statusStyle"
        >
          {{ statusLabel }}
        </span>
      </div>
      <div>
        <p class="decision-detail-panel__label mb-0.5">{{ $t('UserProjects.decisions.detail.escalation') }}</p>
        <span class="text-[13px] font-medium" :style="{ color: escalationStyle.color }">
          {{ escalationLabel }}
        </span>
      </div>
    </div>

    <DecisionEscalationTimeline :decision="decision" />
  </div>
</template>

<script>
import DecisionEscalationTimeline from '@/components/CustomerApp/UserProjects/DecisionEscalationTimeline.vue';
import {
  DECISION_STATUS_STYLES,
  ESCALATION_TIER_STYLES,
  escalationTierFromLevel,
  formatDecisionDate,
  isPendingDecisionStatus,
} from '@/utils/decisionDisplay';

export default {
  name: 'DecisionDetailPanel',
  components: { DecisionEscalationTimeline },
  props: {
    decision: {
      type: Object,
      default: null,
    },
    deciderLabel: {
      type: String,
      default: '—',
    },
    preparerLabel: {
      type: String,
      default: '—',
    },
  },
  emits: ['edit', 'mark-status', 'escalate', 'delete'],
  computed: {
    statusStyle() {
      if (!this.decision) return {};
      const style = DECISION_STATUS_STYLES[this.decision.status] || DECISION_STATUS_STYLES.open;
      return { backgroundColor: style.backgroundColor, color: style.color };
    },
    statusLabel() {
      if (!this.decision) return '';
      const key = DECISION_STATUS_STYLES[this.decision.status]?.labelKey || 'open';
      return this.$t(`ProjectManagement.decisions.status.${key}`);
    },
    escalationStyle() {
      const tier = escalationTierFromLevel(this.decision?.escalationLevel);
      return ESCALATION_TIER_STYLES[tier];
    },
    escalationLabel() {
      return this.$t(`UserProjects.decisions.escalation.${this.escalationStyle.labelKey}`);
    },
    dueLine() {
      const formatted = formatDecisionDate(this.decision?.dueDate, this.$i18n.locale);
      if (this.decision?.isOverdue) {
        return `${formatted} (${this.$t('UserProjects.decisions.card.overdue')})`;
      }
      return formatted;
    },
    isPending() {
      return isPendingDecisionStatus(this.decision?.status);
    },
    isEditable() {
      return this.isPending;
    },
    hasActions() {
      return this.isEditable || this.decision?.status === 'open';
    },
  },
};
</script>

<style scoped>
.decision-detail-panel__label {
  margin: 0 0 0.25rem;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #ababab;
}

.decision-detail-panel__inline-label {
  color: #ababab;
}

.decision-detail-panel__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #e5e5e5;
  border-radius: 0.5rem;
  background: #fff;
  color: var(--color-disabled);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.decision-detail-panel__action:hover {
  background: #fafafa;
  color: var(--color-dark);
}

.decision-detail-panel__action--primary {
  color: var(--color-orange);
  border-color: rgba(228, 130, 26, 0.35);
}

.decision-detail-panel__action--warn {
  color: var(--color-warning-text);
}

.decision-detail-panel__action--danger:hover {
  color: var(--color-error-text);
  border-color: rgba(239, 68, 68, 0.35);
}
</style>
